import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Col,
  Input,
  message,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as ArchiveRestore } from '../../Images/ArchiveRestore.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import customerTypeService from '../../services/customer-type.service';
import ViewCustomerType from './ViewCustomerType';
import eventBus from '../../utils/eventBus';
function ArchiveCustomerTypeList() {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedCustomerType, setSelectedCustomerType] = useState(null);

  const fetchArchiveList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await customerTypeService.getCustomerTypeArchiveList(
        page,
        pagesize,
        search || undefined,
        orderBy || undefined,
      );
      if (response.data) {
        const customerTypes =
          response.data.results?.data ||
          response.data.results ||
          response.data ||
          [];
        setData(Array.isArray(customerTypes) ? customerTypes : []);
        setTotalRecord(response.data.count || 0);
      }
    } catch (error) {
      message.error('Failed to fetch archived customer types');
      console.error('Error fetching archived customer types:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pagesize, search, orderBy]);

  useEffect(() => {
    fetchArchiveList();
  }, [fetchArchiveList]);
  useEffect(() => {
    const handleReload = () => {
      fetchArchiveList();
    };

    eventBus.on('financialYear:refresh', handleReload);

    return () => {
      eventBus.off('financialYear:refresh', handleReload);
    };
  }, [fetchArchiveList]);

  const handleSearch = e => {
    setSearch(e.target.value);
    setPage(1);
  };

  const resetFilter = () => {
    setSearch('');
    setOrderBy('');
    setPage(1);
  };

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const handlePageSizeChange = (current, size) => {
    setPage(1);
    setPageSize(size);
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    if (!sortField || !sortOrder) return 'id';
    const prefix = sortOrder === 'descend' ? '-' : '';
    return `${prefix}${sortField}`;
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
    if (pagination?.pageSize) setPageSize(pagination.pageSize);
    if (pagination?.current) setPage(pagination.current);
  };

  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
    SrNo:(page - 1) * pagesize + index + 1,
  }));

  const handleView = async record => {
    try {
      setLoading(true);
      const response =
        await customerTypeService.getCustomerTypeArchiveById(record.id);
      if (response.data) {
        // Handle different response structures
        const customerTypeData =
          response.data.data || response.data.results || response.data;
        setSelectedCustomerType(customerTypeData);
        setViewOpen(true);
      }
    } catch (error) {
      message.error('Failed to fetch customer type details');
      console.error('Error fetching customer type details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async id => {
    if (!id) {
      message.error('Missing ID for restore!');
      return;
    }

    try {
      await customerTypeService.restoreCustomerType(id);
      message.success('Customer type restored successfully');
      setSelectedRowKeys([]);
      fetchArchiveList();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to restore customer type';
      message.error(errorMessage);
    }
  };

  const handleBulkRestore = async () => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      message.error('Please select at least one record to restore');
      return;
    }

    try {
      await customerTypeService.restoreCustomerType(selectedRowKeys);
      message.success(
        `${selectedRowKeys.length} Customer type(s) restored successfully`,
      );
      setSelectedRowKeys([]);
      fetchArchiveList();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to restore customer types';
      message.error(errorMessage);
    }
  };

  const onViewClose = useCallback(() => {
    setViewOpen(false);
    setSelectedCustomerType(null);
  }, []);

  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      sorter: false,
      width: 80,
    },
    {
      title: 'Customer Type',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 200,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      width: 150,
      render: record => {
        return (
          <span>
            {record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      sorter: true,
      width: 150,
      render: record => {
        return (
          <span>
            {record && typeof record === 'object'
              ? `${record.first_name || ''} ${record.last_name || ''}`.trim() ||
                'N/A'
              : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Archive At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: true,
      width: 150,
      render: record => {
        return (
          <span>
            {record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Archive By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      sorter: true,
      width: 150,
      render: record => {
        return (
          <span>
            {record && typeof record === 'object'
              ? `${record.first_name || ''} ${record.last_name || ''}`.trim() ||
                'N/A'
              : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <span
              onClick={() => handleView(record)}
              style={{ cursor: 'pointer' }}
            >
              <EyeOutlined style={{ fontSize: '17px' }} />
            </span>
          </Tooltip>
          <Tooltip placement="top" title="Restore">
            <Popconfirm
              title="Are you sure you want to restore this customer type?"
              onConfirm={() => handleRestore(record.id)}
              onCancel={() => message.info('Action cancelled')}
              okText="Yes"
              cancelText="No"
            >
              <ArchiveRestore
                className="ArchiverestorImg"
                style={{ cursor: 'pointer', width: 20, height: 20 }}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: selectedKeys => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  return (
    <>
      <div className="sticyaction">
        <Row className="pagenamerow" justify="space-between" align="middle">
          <Col>
            <h2 style={{ margin: 0 }}>Archived Customer Types</h2>
            <div className="bredcrumbwrp">
              <Link to="/customer-type-list" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/customer-type-list">Customer Type Master</Link> },
                  { title: 'Archived Customer Types' },
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space size="large">
              <Input
                placeholder="Search"
                className="searchbox"
                prefix={<SearchOutlined />}
                value={search}
                style={{ width: 200 }}
                onChange={handleSearch}
              />
              <Tooltip
                title={
                  selectedRowKeys.length === 0
                    ? 'Select at least one record to restore'
                    : `Restore ${selectedRowKeys.length} selected record(s)`
                }
              >
                <Popconfirm
                  title={`Are you sure you want to restore ${selectedRowKeys.length} selected customer type(s)?`}
                  onConfirm={handleBulkRestore}
                  onCancel={() => message.info('Action cancelled')}
                  okText="Yes"
                  cancelText="No"
                  disabled={selectedRowKeys.length === 0}
                >
                  <Button
                    type="primary"
                    disabled={selectedRowKeys.length === 0}
                  >
                    Restore Selected
                  </Button>
                </Popconfirm>
              </Tooltip>
              <Tooltip placement="top" title={'Reset Filter'}>
                <Button
                  type="primary"
                  disabled={!search && !orderBy}
                  className="iconlink"
                  onClick={resetFilter}
                >
                  <FilterIcon />
                </Button>
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </div>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <Table
            columns={columns}
            dataSource={dataSource}
            rowKey="id"
            rowSelection={rowSelection}
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content' }}
            onChange={handleTableChange}
            style={{ marginTop: '20px' }}
            locale={{
              emptyText: 'No archived customer types found',
            }}
          />
        </div>
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1000,
            background: '#fff',
            padding: 10,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Pagination
            current={page}
            pageSize={pagesize}
            total={totalRecord}
            showSizeChanger
            onShowSizeChange={handlePageSizeChange}
            showTotal={total => `Total ${total} items`}
            onChange={handlePageChange}
            locale={{ items_per_page: 'Items / Page' }}
          />
        </div>
      </div>

      {viewOpen && (
        <ViewCustomerType
          open={viewOpen}
          onClose={onViewClose}
          customerType={selectedCustomerType}
        />
      )}
    </>
  );
}

export default ArchiveCustomerTypeList;
